import { demoTeam } from './demo-state';
import { MOCK_ROSTER } from './gameplay.mock';
import {
  SCORING_ACTION_KEYS,
  type PlayerPerformance,
  type ScoringActionMap,
  type ScoringRules,
  type TeamFixturePoints,
} from '@/types';

/** Shaped exactly like the API, so screens behave the same either way. */

const emptyActions = (): ScoringActionMap => {
  const map = {} as ScoringActionMap;
  for (const key of SCORING_ACTION_KEYS) map[key] = 0;
  return map;
};

/** The API's default scoring rates. */
export const MOCK_SCORING_RULES: ScoringRules = {
  ...emptyActions(),
  id: 'rules_demo',
  status: 'active',
  appearance: 1,
  minutes_60: 1,
  goal_outside_box: 1,
  assist: 3,
  balls_recovered: 1,
  balls_recovered_every: 3,
  player_of_the_match: 3,
  penalty_won: 2,
  penalty_conceded: -1,
  penalty_missed: -2,
  yellow_card: -1,
  red_card: -3,
  own_goal: -2,
  goal_gk: 6,
  goal_def: 6,
  goal_mid: 5,
  goal_fwd: 4,
  penalty_save: 5,
  clean_sheet_gk: 4,
  clean_sheet_def: 4,
  clean_sheet_mid: 1,
  saves: 1,
  saves_every: 3,
  goals_conceded_gk: -1,
  goals_conceded_def: -1,
  goals_conceded_every: 2,
};

/** Build one scored row from the actions a player actually recorded. */
function performance(
  fixtureId: number,
  playerId: number,
  actions: Partial<ScoringActionMap>,
  points: Partial<ScoringActionMap>,
): PlayerPerformance {
  const source = MOCK_ROSTER.find((player) => player.playerId === playerId);
  const scored = { ...emptyActions(), ...points };

  return {
    id: `perf_${fixtureId}_${playerId}`,
    seriesId: 900001,
    fixtureId,
    playerId,
    teamId: source?.teamId ?? 900101,
    opponentTeamId: source?.teamId === 900101 ? 900102 : 900101,
    ...(source?.position ? { position: source.position } : {}),
    wasHome: true,
    playerName: source?.displayName ?? source?.name ?? `Player ${playerId}`,
    ...(source?.teamName ? { teamName: source.teamName } : {}),
    ...(source?.teamShortCode ? { teamShortCode: source.teamShortCode } : {}),
    performance: { ...emptyActions(), ...actions },
    points: scored,
    totalPoints: Object.values(scored).reduce((sum, value) => sum + value, 0),
    status: 'final',
  };
}

/**
 * Sample scores on the two played fixtures.
 *
 * Includes a negative row — a goalkeeper who conceded — so the demo exercises
 * the losing side of the scoring UI, not only the flattering one.
 */
export const MOCK_PERFORMANCES: readonly PlayerPerformance[] = [
  performance(
    900001,
    901002,
    { appearance: 1, minutes_60: 1, goal_mid: 1, assist: 1, balls_recovered: 6 },
    { appearance: 1, minutes_60: 1, goal_mid: 5, assist: 3, balls_recovered: 2 },
  ),
  performance(
    900001,
    901003,
    { appearance: 1, minutes_60: 1, goal_fwd: 1 },
    { appearance: 1, minutes_60: 1, goal_fwd: 4 },
  ),
  performance(
    900001,
    901001,
    { appearance: 1, minutes_60: 1, clean_sheet_def: 1 },
    { appearance: 1, minutes_60: 1, clean_sheet_def: 4 },
  ),
  performance(
    900001,
    901000,
    { appearance: 1, minutes_60: 1, saves: 3, goals_conceded_gk: 2, yellow_card: 1 },
    { appearance: 1, minutes_60: 1, saves: 1, goals_conceded_gk: -1, yellow_card: -1 },
  ),
  performance(
    900002,
    901007,
    { appearance: 1, minutes_60: 1, assist: 1, penalty_won: 1 },
    { appearance: 1, minutes_60: 1, assist: 3, penalty_won: 2 },
  ),
  performance(
    900002,
    901006,
    { appearance: 1, minutes_60: 1, player_of_the_match: 1, goal_mid: 1 },
    { appearance: 1, minutes_60: 1, player_of_the_match: 3, goal_mid: 5 },
  ),
];

/**
 * The demo team's return from a fixture.
 *
 * Built from whatever eleven was entered this session, so the numbers match the
 * team on screen rather than a fixed sample.
 */
export function mockTeamFixturePoints(teamId: string, fixtureId: number): TeamFixturePoints {
  const team = demoTeam();
  const scored = new Map(
    MOCK_PERFORMANCES.filter((row) => row.fixtureId === fixtureId).map((row) => [
      row.playerId,
      row,
    ]),
  );

  const players = (team?.players ?? []).map((player) => {
    const row = scored.get(player.playerId);
    const isCaptain = player.playerId === team?.captainPlayerId;
    const isViceCaptain = player.playerId === team?.vicePlayerId;

    if (!row) {
      return {
        playerId: player.playerId,
        isCaptain,
        isViceCaptain,
        totalPoints: 0,
        appliedPoints: 0,
        performance: null,
        points: null,
        playerName: player.displayName ?? player.name,
        ...(player.teamName ? { teamName: player.teamName } : {}),
        position: player.position,
      };
    }

    return {
      ...row,
      isCaptain,
      isViceCaptain,
      appliedPoints: isCaptain ? row.totalPoints * 2 : row.totalPoints,
    };
  });

  return {
    teamId,
    contestId: team?.contestId ?? 'ct_weekly',
    fixtureId,
    captainPlayerId: team?.captainPlayerId ?? 0,
    vicePlayerId: team?.vicePlayerId ?? 0,
    teamFixturePoints: players.reduce((sum, player) => sum + player.appliedPoints, 0),
    missingPlayerIds: players.filter((p) => p.points === null).map((p) => p.playerId),
    players,
  };
}
